import React from "react";
import PropTypes from "prop-types";
import AmCharts from "@amcharts/amcharts3-react";
import SmartTimeSeriesRenderer from "../SmartTimeSeriesRenderer";

let getMostRecentRango = function(date, ranges) {
  var mostRecentRango = null;
  ranges.forEach(function(rango, index, array) {
    var fecha = new Date(rango.Fecha);
    rango.date = fecha;
    if (date > fecha) {
      if (mostRecentRango == null) {
        mostRecentRango = rango;
      } else if (fecha > mostRecentRango.date) {
        mostRecentRango = rango;
      }
    }
  });
  return mostRecentRango;
};

let makeDataProvider = function(data, ranges, lang) {
  let dataProvider = [];
  let filteredData = data.filter(function(s) {
    return s.sensoresTemperatura !== undefined;
  });
  filteredData.forEach(function(ubicacion, index, array) {
    var newUbi = {};
    newUbi.date = new Date(ubicacion.Fecha);
    newUbi.fecha = newUbi.date.toString();
    var sensoresActivos = 0;
    var suma = 0;
    if (ubicacion.sensoresTemperatura[0] > -1000) {
      newUbi.sensor1 = ubicacion.sensoresTemperatura[0];
      sensoresActivos++;
      suma = suma + newUbi.sensor1;
    }
    if (ubicacion.sensoresTemperatura[1] > -1000) {
      newUbi.sensor2 = ubicacion.sensoresTemperatura[1];
      sensoresActivos++;
      suma = suma + newUbi.sensor2;
    }
    if (ubicacion.sensoresTemperatura[2] > -1000) {
      newUbi.sensor3 = ubicacion.sensoresTemperatura[2];
      sensoresActivos++;
      suma = suma + newUbi.sensor3;
    }
    if (ubicacion.sensoresTemperatura[3] > -1000) {
      newUbi.sensor4 = ubicacion.sensoresTemperatura[3];
      sensoresActivos++;
      suma = suma + newUbi.sensor4;
    }

    var rangoReciente = getMostRecentRango(newUbi.date, ranges);

    if (rangoReciente) {
      newUbi.inferior = rangoReciente.LimiteInferior;
      newUbi.superior = rangoReciente.LimiteSuperior;
    }

    if (ubicacion.hasOwnProperty("Evento")) {
      if ([101, 102].indexOf(ubicacion.Evento) >= 0) {
        newUbi.evento = ubicacion.Evento;
        newUbi.nombreEvento = lang["Event" + ubicacion.Evento];
      }
    } else {
      newUbi.evento = 0;
    }

    if (newUbi.sensor1 || newUbi.sensor2 || newUbi.sensor3 || newUbi.sensor4) {
      newUbi.promedio = (suma / sensoresActivos).toFixed(1);
      dataProvider.push(newUbi);
    }
  });
  return dataProvider;
};

let makeChartGuides = function(data) {
  let guides = [];
  data.forEach(function(ubicacion) {
    var color = "#000000";
    if (ubicacion.Evento === 101) {
      color = "#a5cf35";
    } else if (ubicacion.Evento === 102) {
      color = "#c44b4b";
    }

    if (ubicacion.Evento === 101 || ubicacion.Evento === 102) {
      guides.push({
        date: ubicacion.Fecha,
        lineColor: color,
        gid: "Evento",
        lineAlpha: 1,
        dashLength: 3,
        lineThickness: 3,
        inside: true,
        labelRotation: 90,
        label: ""
      });
    }
  });
  return guides;
};

let showGuides = function(legendEvent) {
  let ref = legendEvent.chart;
  ref.chartDiv.style.cursor = "progress";
  ref.categoryAxis.guides.forEach(function(element) {
    if (element.gid === legendEvent.dataItem.id) {
      element.lineAlpha = 1;
    }
  });
  ref.invalidateSize(); // mucho mas ligero que validateNow
};

let hideGuides = function(legendEvent) {
  let ref = legendEvent.chart;
  ref.chartDiv.style.cursor = "progress";
  ref.categoryAxis.guides.forEach(function(element) {
    if (element.gid === legendEvent.dataItem.id) {
      element.lineAlpha = 0;
    }
  });
  ref.invalidateSize(); // mucho mas ligero que validateNow
};

const Temperatura = ({ data, ranges, lang }) => {
  let ubicaciones = [...data];
  let rangos = [...ranges];
  let dataProvider = makeDataProvider(ubicaciones, rangos, lang);
  let guides = makeChartGuides(ubicaciones);

  let config = {
    type: "serial",
    theme: "light",
    pathToImages: "https://cdn.amcharts.com/lib/3/images/",
    language: lang._code,
    marginTop: 0,
    marginRight: 30,
    listeners: [
      {
        event: "drawn",
        method: function(e) {
          e.chart.chartDiv.style.cursor = "initial";
        }
      }
    ],
    legend: {
      equalWidths: true,
      position: "bottom",
      valueAlign: "left",
      maxColumns: 7,
      fontSize: 14,
      spacing: 10,
      align: "center",
      backgroundColor: "#000000",
      backgroundAlpha: 0,
      listeners: [
        { event: "showItem", method: showGuides },
        { event: "hideItem", method: hideGuides }
      ]
    },
    valueAxes: [
      {
        axisAlpha: 0,
        position: "left"
      }
    ],
    graphs: [
      {
        id: "inferior",
        bulletSize: 8,
        title: "",
        showBalloon: false,
        lineColor: "#000000",
        fillAlphas: 0,
        lineAlpha: 0,
        valueField: "inferior",
        hidden: true,
        visibleInLegend: false
      },
      {
        id: "superior",
        bulletSize: 8,
        title: lang.Rango + ":",
        showBalloon: false,
        fillToGraph: "inferior",
        lineColor: "#2ecc71",
        fillAlphas: 0.2,
        lineAlpha: 0,
        valueField: "superior",
        legendValueText: "[[inferior]] / [[superior]]"
      },
      {
        id: "sensor1",
        hideBulletsCount: 1,
        title: "Sensor 1:",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineColor: "#F66B34",
        lineThickness: 3,
        valueField: "sensor1"
      },
      {
        id: "sensor2",
        hideBulletsCount: 1,
        title: "Sensor 2:",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineColor: "#3482AA",
        lineThickness: 3,
        valueField: "sensor2"
      },
      {
        id: "sensor3",
        hideBulletsCount: 1,
        title: "Sensor 3:",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineColor: "#775ADA",
        lineThickness: 3,
        valueField: "sensor3"
      },
      {
        id: "sensor4",
        hideBulletsCount: 1,
        title: "Sensor 4:",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineColor: "#1A936F",
        lineThickness: 3,
        valueField: "sensor4"
      },
      {
        id: "promedio",
        hideBulletsCount: 1,
        title: lang.Promedio + ":",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineColor: "#555555",
        lineThickness: 3,
        valueField: "promedio"
      },
      {
        id: "Evento",
        hideBulletsCount: 1,
        title: lang.Evento + ":",
        showBalloon: false,
        bullet: "round",
        bulletSize: 8,
        lineAlpha: 0,
        lineColor: "#444444",
        lineThickness: 3,
        valueField: "promedio",
        legendValueText: "[[nombreEvento]]"
      }
    ],
    chartScrollbar: {
      graph: "g5",
      gridAlpha: 0,
      color: "#888888",
      scrollbarHeight: 55,
      backgroundAlpha: 0,
      selectedBackgroundAlpha: 0.1,
      selectedBackgroundColor: "#888888",
      graphFillAlpha: 0,
      autoGridCount: true,
      selectedGraphFillAlpha: 0,
      graphLineAlpha: 0.2,
      graphLineColor: "#c2c2c2",
      selectedGraphLineColor: "#888888",
      selectedGraphLineAlpha: 1
    },
    mouseWheelZoomEnabled: true,
    chartCursor: {
      cursorPosition: "mouse",
      cursorColor: "#F05A30",
      categoryBalloonDateFormat: "YYYY-MM-DD HH:NN:SS"
    },
    categoryField: "fecha",
    categoryAxis: {
      minPeriod: "mm",
      parseDates: true,
      startOnAxis: true,
      axisColor: "#DADADA",
      gridAlpha: 0.07,
      title: "Fecha",
      guides: guides
    },
    dataProvider: dataProvider
  };

  return (
    <AmCharts.React
      style={{
        width: "100%",
        height: "100%"
      }}
      options={config}
    />
  );
};

const TempTimeseries = ({ data, ranges, lang }) => (
  <SmartTimeSeriesRenderer
    chart={Temperatura}
    data={data}
    ranges={ranges}
    lang={lang}
  />
);

TempTimeseries.propTypes = {
  data: PropTypes.array.isRequired,
  ranges: PropTypes.array.isRequired,
  lang: PropTypes.object.isRequired
};

export default TempTimeseries;



// WEBPACK FOOTER //
// ./src/charts/TempTimeseries/TempTimeseriesAmcharts.js